import React, { useMemo, useRef, useEffect, useCallback } from "react";
import { Box, Chip, Grid, Typography } from "@mui/material";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import { colors, ReviewBadge, StyledTooltip } from "./SubtopicCard";
import {
  List as RVList,
  AutoSizer,
  CellMeasurer,
  CellMeasurerCache,
  ListRowProps,
} from "react-virtualized";
import { singleLineEllipsis } from "../../Components/AllCards/layouts/CardHeader";

/** ---------- Reusable Row Component ---------- */
const ClusterRow: React.FC<{
  cluster: any;
  color: string;
  filters: any;
  setFilters: (cb: (prev: any) => any) => void;
  style?: React.CSSProperties;
  registerChild?: (el: HTMLElement | null) => void;
}> = ({ cluster, color, filters, setFilters, style, registerChild }) => {
  const {
    name,
    id,
    reviewed,
    pending,
    save_for_later,
    type,
    lod_index,
    description,
    primary_language,
    max_time,
    min_time,
    by_sme,
    exposure_index,
    accuracy_index,
    retention_index,
  } = cluster;

  return (
    <div style={style} ref={registerChild ?? undefined}>
      <Box sx={{ mt: 1 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            p: "6px 10px",
            borderRadius: "6px",
            "&:hover": filters.cluster_id === id ? {} : { bgcolor: "#f9f9f9" },
            bgcolor: filters.cluster_id === id ? "rgb(166 166 166)" : "inherit",
            cursor: "pointer",
          }}
          onClick={() => {
            setFilters((prev: any) => ({
              ...prev,
              cluster,
              cluster_id: id,
              cluster_color: color,
            }));
          }}
        >
          {/* Left side */}
          <Box sx={{ flex: 8, display: "flex", alignItems: "center" }}>
            <Box
              sx={{
                background: color,
                mr: 1,
                borderRadius: "50%",
                height: 7,
                width: 7,
                minHeight: 7,
                minWidth: 7,
              }}
            />
            <span
              style={{
                fontSize: "0.9rem",
                fontWeight: filters.cluster_id === id ? "bold" : 500,
              }}
            >
              {name}
            </span>
          </Box>

          {/* Right side */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Chip
              label={`id: #${id}`}
              size="small"
              sx={{
                border: "1px solid rgb(0 0 0 / 60%)",
                borderRadius: "4px",
                padding: "0",
                color: "rgb(0 0 0 / 60%)",
                fontSize: "0.75rem",
                background: "transparent",
                "& .MuiChip-label": {
                  padding: "0 3px !important",
                },
                ...singleLineEllipsis,
              }}
            />
            <ReviewBadge
              reviewed={reviewed}
              total={pending + reviewed + save_for_later}
              save_for_later={save_for_later}
              pending={pending}
            />
            <StyledTooltip
              arrow
              placement="top"
              title={
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    p: 2,
                    borderRadius: 2,
                    boxShadow: 1,
                    bgcolor: "background.paper",
                    maxWidth: 480,
                    minWidth: 320,
                  }}
                >
                  {/* Header */}
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    sx={{ mb: 1, color: "text.primary" }}
                  >
                    {cluster.name}  Details <Chip
              label={`id: #${cluster.id}`}
              size="small"
              sx={{
                border: "1px solid rgb(0 0 0 / 60%)",
                borderRadius: "4px",
                padding: "0",
                color: "rgb(0 0 0 / 60%)",
                fontSize: "0.75rem",
                background: "transparent",
                "& .MuiChip-label": {
                  padding: "0 3px !important",
                },
                ...singleLineEllipsis,
              }}
            />
                  </Typography>

                  {/* Grid details */}
                  <Grid container spacing={1.5}>
                    <Grid item xs={4}>
                      <Typography variant="caption" fontWeight={500}>
                        Type
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {type}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="caption" fontWeight={500}>
                        LOD Index
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {lod_index}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="caption" fontWeight={500}>
                        Primary Language
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {primary_language}
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <Typography variant="caption" fontWeight={500}>
                        Description
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {description}
                      </Typography>
                    </Grid>

                    <Grid item xs={4}>
                      <Typography variant="caption" fontWeight={500}>
                        Time
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {min_time} – {max_time}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="caption" fontWeight={500}>
                        By SME
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {by_sme ? "Yes" : "No"}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="caption" fontWeight={500}>
                        Exposure Index
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {exposure_index || "-"}
                      </Typography>
                    </Grid>

                    <Grid item xs={4}>
                      <Typography variant="caption" fontWeight={500}>
                        Accuracy Index
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {accuracy_index || "-"}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="caption" fontWeight={500}>
                        Retention Index
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {retention_index || "-"}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              }
            >
              <InfoOutlined fontSize="small" />
            </StyledTooltip>
          </Box>
        </Box>
      </Box>
    </div>
  );
};

/** ---------- Main ClusterList ---------- */
interface ClusterListProps {
  clusters: any[];
  open: string | number | null;
  id: string | number;
  filters: any;
  setFilters: (cb: (prev: any) => any) => void;
  localFilterState: {
    topic: string;
    subtopic: string;
    cluster: string;
  };
}

const ClusterList: React.FC<ClusterListProps> = ({
  clusters,
  open,
  id,
  filters,
  setFilters,
  localFilterState,
}) => {
  const filteredClusters = useMemo(() => {
    return clusters
      .filter((s) => s.pending + s.reviewed + s.save_for_later !== 0)
      .filter((s) =>
        localFilterState.cluster !== "all"
          ? localFilterState.cluster === "pending"
            ? s.pending + s.save_for_later !== 0
            : s.pending + s.reviewed + s.save_for_later !== 0
          : true
      );
  }, [clusters, localFilterState.cluster]);

  const cacheRef = useRef(
    new CellMeasurerCache({
      defaultHeight: 64,
      fixedWidth: true,
    })
  );
  const listRef = useRef<RVList | null>(null);
  const rowRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  // clear & recompute cache on data changes
  useEffect(() => {
    cacheRef.current.clearAll();
    (listRef.current as any)?.recomputeRowHeights?.(0);
    (listRef.current as any)?.forceUpdateGrid?.();
  }, [clusters.length, localFilterState.subtopic]);

  // scroll into view whenever selected cluster_id changes
  useEffect(() => {
    if (!filters.cluster_id) return;

    const index = filteredClusters.findIndex(
      (c) => c.id === filters.cluster_id
    );
    if (index === -1) return;

    if (filteredClusters.length > 20) {
      listRef.current?.scrollToRow(index);
    } else {
      rowRefs.current[filters.cluster_id]?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [filters.cluster_id, filteredClusters]);

  const rowRenderer = useCallback(
    ({ index, key, style, parent }: ListRowProps) => {
      const cluster = filteredClusters[index];
      if (!cluster) return null;
      const color = colors[index % colors.length];

      return (
        <CellMeasurer
          key={key}
          cache={cacheRef.current}
          parent={parent}
          columnIndex={0}
          rowIndex={index}
        >
          {({ registerChild }) => (
            <ClusterRow
              cluster={cluster}
              color={color}
              filters={filters}
              setFilters={setFilters}
              style={style}
              registerChild={registerChild}
            />
          )}
        </CellMeasurer>
      );
    },
    [filteredClusters, filters, setFilters]
  );

  if (open !== id) return null;

  return filteredClusters.length > 24 ? (
    <Box
      sx={{
        width: "100%",
        height: "calc(100vh -  362.53px)",
        //  "378px !important"
      }}
    >
      <AutoSizer>
        {({ width, height }) => (
          <RVList
            className="scrollBluePrimarythin" // add your custom class here
            ref={(ref) => {
              listRef.current = ref;
            }}
            width={width}
            height={height}
            deferredMeasurementCache={cacheRef.current}
            rowHeight={cacheRef.current.rowHeight}
            rowCount={filteredClusters.length}
            rowRenderer={rowRenderer}
            overscanRowCount={5}
          />
        )}
      </AutoSizer>
    </Box>
  ) : (
    <Box>
      {filteredClusters.map((cluster, index) => (
        <div key={cluster.id} ref={(el) => (rowRefs.current[cluster.id] = el)}>
          <ClusterRow
            cluster={cluster}
            color={colors[index % colors.length]}
            filters={filters}
            setFilters={setFilters}
          />
        </div>
      ))}
    </Box>
  );
};

export default ClusterList;
